// @flow

import { Record, type RecordOf } from 'immutable';
import * as React from 'react';

import { HEIGHT, WIDTH } from './constant';

type Facing = 'left' | 'right';
type CustomerProps = {
  kind: string,
  subKind: number,
  facing: Facing,
  animationDelay: number,
  animationDuration: number,
  offsetY: number,
};
type CustomerType = RecordOf<{ ...CustomerProps, animationIterationCount: string }>;

const W_H_RATIO = 150 / 240;

const makeCustomer = Record({
  kind: 0,
  subKind: 1,
  facing: 'left',
  animationDelay: 0,
  animationDuration: 0,
  animationIterationCount: '1',
  offsetY: 0,
});

const genCustomer = (
  kind: string,
  subKind: number,
  flip: boolean,
  loop: boolean,
  index: number,
  delay: number
): CustomerType => {
  const facing = !flip ? 'left' : 'right';
  const animationDelay = index ? delay * (index - 0.1) + delay * 0.8 * Math.random() : 0;
  const animationDuration = 8;
  const animationIterationCount = loop ? 'infinite' : '1';
  const offsetY = Math.random();

  return makeCustomer({
    facing,
    animationDelay,
    animationDuration,
    animationIterationCount,
    offsetY,
    subKind,
    kind,
  });
};

const Customer = (props: {
  scale: number,
  customer: CustomerType,
  // onAnimationIteration: (*) => void,
}): React.Node => {
  let style = {
    height: `${props.scale * HEIGHT * 0.5}px`,
    width: `${props.scale * HEIGHT * 0.5 * W_H_RATIO}px`,
    animationDelay: `${props.customer.animationDelay}s`,
    animationIterationCount: props.customer.animationIterationCount,
  };
  let classNames = ['customer-wrapper'];
  if (props.customer.facing === 'right') {
    classNames.push('customer-flip');
  }
  const className = classNames.join(' ');

  let customerKind = '';
  switch (props.customer.kind) {
    case '0':
      customerKind = 'male';
      break;
    case '1':
      customerKind = 'female';
      break;
    case '2':
      customerKind = 'kidsm';
      break;
    case '3':
      customerKind = 'kidsfe';
      break;
    default:
      customerKind = 'unknown';
      break;
  }

  return (
    <div
      className={className}
      style={style}
      // onAnimationIteration={props.onAnimationIteration}
    >
      <div
        style={{
          animationDelay: `${props.customer.animationDelay}s`,
        }}
        className={`customer customer-${customerKind}-0${props.customer.subKind} `}
      />
    </div>
  );
};

type Props = {
  // customerCounts: Map<string, number>,
  bottom: number,
  scale: number,
  customers: Array<{ kind: string, subKind: number }>,
  flip?: boolean,
  loop?: boolean,
  onAnimationEnd?: () => void,
};

export default function CustomerSimulation(props: Props) {
  let { customers, loop = false, flip = false, scale, bottom, onAnimationEnd } = props;

  let customerTypes = React.useMemo(() => {
    const delay = 8 / (customers.length || 1);
    return customers.map((customer, i) => genCustomer(customer.kind, customer.subKind, flip, loop, i, delay));
  }, [customers, flip, loop]);

  let style = React.useMemo(
    () => ({
      bottom: scale * bottom * HEIGHT,
      height: `${scale * 0.5 * HEIGHT}px`,
      width: `${scale * WIDTH}px`,
    }),
    [scale, bottom]
  );

  return (
    <div className="customer-simulation" style={style} onAnimationEnd={onAnimationEnd}>
      {customerTypes.map(customer => (
        <Customer key={customer.hashCode()} scale={scale} customer={customer} />
      ))}
    </div>
  );
}
